import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";

/** Sanfte E-Mail-Prüfung: Format-Hinweis und Tippfehler bei bekannten Anbietern (nicht blockierend). */

export type EmailHint = {
  message: string;
  suggestion?: string;
};

export type EmailHintMessages = Pick<
  BookingMessages["validation"],
  "emailIncomplete" | "emailUnusual"
>;

/** Große und häufig genutzte Mail-Anbieter (DE/EU/international). */
export const KNOWN_EMAIL_DOMAINS: readonly string[] = [
  "gmail.com",
  "googlemail.com",
  "gmx.de",
  "gmx.net",
  "gmx.at",
  "gmx.ch",
  "web.de",
  "t-online.de",
  "outlook.com",
  "outlook.de",
  "hotmail.com",
  "hotmail.de",
  "live.de",
  "live.com",
  "msn.com",
  "yahoo.com",
  "yahoo.de",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "aol.de",
  "mail.de",
  "freenet.de",
  "posteo.de",
  "proton.me",
  "protonmail.com",
  "arcor.de",
  "vodafone.de",
  "kabelmail.de",
  "unitybox.de",
  "online.de",
  "1und1.de",
  "bluewin.ch",
  "sunrise.ch",
  "aon.at",
  "mailbox.org",
  "tutanota.com",
  "tuta.io",
  "zoho.com",
  "mail.com",
  "email.de",
  "gmx.com",
  "yahoo.fr",
  "yahoo.co.uk",
  "hotmail.co.uk",
  "outlook.fr",
  "laposte.net",
  "orange.fr",
  "free.fr",
  "sfr.fr",
  "alice.it",
  "libero.it",
  "tin.it",
  "virgilio.it",
  "wp.pl",
  "o2.pl",
  "interia.pl",
  "seznam.cz",
  "centrum.cz",
  "abv.bg",
  "mail.ru",
  "yandex.ru",
  "yandex.com",
  "bk.ru",
  "list.ru",
  "inbox.ru",
];

/** Häufige Tippfehler → korrekte Domain. */
const DOMAIN_TYPOS: Record<string, string> = {
  "gnail.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "gmail.om": "gmail.com",
  "gmail.de": "gmail.com",
  "googlemail.de": "googlemail.com",
  "goglemail.com": "googlemail.com",
  "gmx.cm": "gmx.de",
  "gmz.de": "gmx.de",
  "gnx.de": "gmx.de",
  "web.com": "web.de",
  "web.dr": "web.de",
  "wab.de": "web.de",
  "t-online.com": "t-online.de",
  "tonline.de": "t-online.de",
  "t-onlne.de": "t-online.de",
  "t-onine.de": "t-online.de",
  "outlok.com": "outlook.com",
  "outllok.com": "outlook.com",
  "outlook.cm": "outlook.com",
  "outlook.con": "outlook.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmail.cm": "hotmail.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yaho.de": "yahoo.de",
  "iclod.com": "icloud.com",
  "icloud.con": "icloud.com",
  "protonmail.con": "protonmail.com",
  "freenet.com": "freenet.de",
  "posteo.com": "posteo.de",
};

const KNOWN_DOMAIN_SET = new Set(KNOWN_EMAIL_DOMAINS);

const BASIC_EMAIL_RE =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => 0),
  );

  for (let i = 0; i < rows; i++) matrix[i][0] = i;
  for (let j = 0; j < cols; j++) matrix[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }
  return matrix[rows - 1][cols - 1];
}

function parseEmailParts(email: string): { local: string; domain: string } | null {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  return {
    local: trimmed.slice(0, at),
    domain: trimmed.slice(at + 1),
  };
}

function findClosestKnownDomain(domain: string): string | null {
  if (KNOWN_DOMAIN_SET.has(domain)) return null;

  const typo = DOMAIN_TYPOS[domain];
  if (typo) return typo;

  let best: string | null = null;
  let bestDistance = 3;

  for (const known of KNOWN_EMAIL_DOMAINS) {
    if (Math.abs(known.length - domain.length) > 2) continue;
    const distance = levenshtein(domain, known);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = known;
    }
  }

  return bestDistance <= 2 ? best : null;
}

/** Prüft das Format – nur für Hinweise, nicht zum Blockieren des Abschlusses. */
export function looksLikeInvalidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return false;
  if (!trimmed.includes("@")) return trimmed.length >= 3;
  return !BASIC_EMAIL_RE.test(trimmed);
}

function emailTypoMessage(suggestion: string, locale: "de" | "en" = "de"): string {
  if (locale === "en") {
    return `Did you mean "${suggestion}"? Please check your email address.`;
  }
  return `Meinten Sie vielleicht „${suggestion}"? Bitte prüfen Sie Ihre E-Mail-Adresse.`;
}

/** Hinweis bei Tippfehlern oder ungewöhnlichem Format; null = kein Hinweis. */
export function getEmailHint(
  email: string,
  messages: EmailHintMessages = de.validation,
  locale: "de" | "en" = "de",
): EmailHint | null {
  const trimmed = email.trim();
  if (!trimmed) return null;

  const parts = parseEmailParts(trimmed);
  if (!parts) {
    if (trimmed.includes("@") && trimmed.length >= 4) {
      return {
        message: messages.emailIncomplete,
      };
    }
    return null;
  }

  const suggestedDomain = findClosestKnownDomain(parts.domain);
  if (suggestedDomain) {
    const suggestion = `${parts.local}@${suggestedDomain}`;
    return {
      message: emailTypoMessage(suggestion, locale),
      suggestion,
    };
  }

  if (looksLikeInvalidEmail(trimmed)) {
    return {
      message: messages.emailUnusual,
    };
  }

  return null;
}
